// eslint-disable-next-line import/no-unresolved, import/no-extraneous-dependencies
import {
	ColumnFilterOptions,
	ColumnFilterValue,
	Pagination,
	ResolvedResponse,
	Sort2 as Sort,
	Status,
	Translations,
} from '@innovyze/lib_am_common';
import { createAction } from '@reduxjs/toolkit';
import {
	CreateConfig,
	InitRiskMatrixPreviewPayload,
	LogReply,
	RiskCalcStatusResult,
	RiskConfig,
	SimulationData,
	RiskGradeRanges,
	RiskMatrixGridObj,
	RiskMatrixObj,
	TaskData,
	PositionObj,
	OverallRiskSetting,
} from '@Types/risk.types';
// eslint-disable-next-line import/no-unresolved, import/no-extraneous-dependencies
import { paginationInitialState } from '@innovyze/lib_am_common/Reducers';
import { addNamespace } from '@Utils/actions';
import { ConfigPublishState } from '@Types/riskConfig.types';
import {
	ComponentCount,
	ComponentMetadata,
	MultiParamCell,
} from '@Types/component.types';
import {
	IsCountLoadingChange,
	RiskCountChange,
	RiskCountRename,
} from '@Reducers/Risk.reducers';

export const paginationDefault = (): Pagination => ({
	...paginationInitialState,
	sortKey: 'CURRENT_RISK',
	sortDescending: true,
});

export const getRiskConfig = createAction<string>('Risk/getRiskConfig');
export const getRiskConfigResolved = createAction<RiskConfig>(
	'Risk/getRiskConfigResolved',
);
export const getRiskConfigRejected = createAction('Risk/getRiskConfigRejected');
export const clearRiskConfig = createAction('Risk/clearRiskConfig');

export const createRiskConfig = createAction<{
	history: unknown;
	config: CreateConfig;
	page: number;
}>('Risk/createRiskConfig');

export const changeRiskConfigPublishState = createAction<{
	newPublishState: ConfigPublishState;
	configId: string;
	refresh: () => void;
}>('Risk/changeRiskConfigPublishState');

export const duplicateRiskConfig = createAction<{
	onComplete: (configId: string) => void;
	configId: string;
}>('Risk/duplicateRiskConfig');

export const setRiskConfigIsCreating = createAction<boolean>(
	'riskSetup/setRiskConfigIsCreating',
);

export const setRiskConfigNameError = createAction<string>(
	'riskSetup/setRiskConfigNameError',
);

export const renameRiskConfigAction = createAction<RiskConfig>(
	'Risk/renameRiskConfig',
);

export const saveRiskConfigAction = createAction<RiskConfig>(
	'Risk/saveRiskConfig',
);

export const getRiskDetails = createAction<Pagination>('risk/getRiskDetails');

export const runRisk = createAction<string>('risk/runRisk');
export const runRiskRejected = createAction('risk/runRiskRejected');
export const runRiskResolved = createAction('risk/runRiskResolved');

export const resetRisk = createAction<string>('risk/resetRisk');
export const resetRiskRejected = createAction('risk/resetRiskRejected');
export const resetRiskResolved = createAction('risk/resetRiskResolved');

export const getRiskDetailsDefaulted = () =>
	getRiskDetails(paginationDefault());
export const reloadRiskDetails = createAction<string>('risk/reloadRiskDetails');
export const getRiskDetailsResolved = createAction<ResolvedResponse>(
	'riskdetails/getRiskDetails/resolved',
);
export const getRiskDetailsRejected = createAction<Status>(
	'riskdetails/getRiskDetails/rejected',
);

export const changePagination = createAction<{ page?: number; slice?: number }>(
	'riskdetails/paginationModel',
);

export const clearRiskScanId = createAction(
	addNamespace('riskdetails/clearRiskScanId'),
);

export const changeSlice = createAction<number>('riskdetails/slice');

export const changePage = createAction<number>('riskdetails/page');

export const changeSearch = createAction<string>('riskdetails/changeSearch');

export const clearScanId = createAction<string>('riskdetails/clearScanId');

export const changeSort = createAction<Sort>('riskdetails/sort');

export const setSelected = createAction<string[]>('riskdetails/selected/set');

export const addSelected = createAction<string[]>('riskdetails/selected/add');

export const removeSelected = createAction<string[]>(
	'riskdetails/selected/remove',
);

export const clearSelected = createAction('riskdetails/selected/clear');

export const currentRow = createAction<number>('riskdetails/current/row');

export const setRiskDetailsIsBusy = createAction<boolean>('riskdetails/isBusy');
export const setTab = createAction<string>('Risk/setTab');
export const setSaveRun = createAction<boolean>('Risk/setSaveRun');

export const setRiskCalcStatus = createAction<RiskCalcStatusResult>(
	'Risk/setRiskCalcStatus',
);

export const getLog = createAction<string>('Log/getLog');
export const getLogResolved = createAction<LogReply>('Log/getLogResolved');
export const getLogRejected = createAction('Log/getLogRejected');
export const getSimulationData = createAction('Risk/getSimulationData');
export const getSimulationDataResolved = createAction<SimulationData>(
	'Risk/getSimulationDataResolved',
);

export const getTaskData = createAction<string>('Risk/getTaskData');
export const getTaskDataResolved = createAction<TaskData>(
	'Risk/getTaskDataResolved',
);

export const changeFilterSelect = createAction<ColumnFilterValue>(
	addNamespace('riskDetails/filterSelect'),
);
export const changeFiltersSelect = createAction<ColumnFilterValue[]>(
	addNamespace('riskDetails/filtersSelect'),
);

export const updateColumnFilterSelectOptions =
	createAction<ColumnFilterOptions>(
		addNamespace('riskDetails/filterSelect/column/options'),
	);

export const translate = createAction<Translations>(
	addNamespace('riskDetails/translate'),
);

export const setRiskSetupDirty = createAction<boolean>(
	'riskSetup/setRiskSetupDirty',
);

export const setRunCompleteReload = createAction<boolean>(
	'Risk/riskCompleteReload',
);

export const setRefreshList = createAction<boolean>('Risk/setRefreshList');

export const setRiskIsSaving = createAction<boolean>(
	'riskSetup/setRiskIsSaving',
);

export const deleteRisk = createAction<{
	configId: string;
	refresh: () => void;
}>('rehab/deleteRisk');
export const setRiskIsDeleting = createAction<boolean>(
	'risk/setRiskIsDeleting',
);

export const setRiskCountIsLoading = createAction<IsCountLoadingChange>(
	'Risk/setRiskCountIsLoading',
);

export const getRiskCountInspection = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	fieldName: string;
	inspectionType: string;
}>('Risk/getRiskCountInspection');

export const getRiskCountAttribute = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	fieldName: string;
}>('Risk/getRiskCountAttribute');

export const getRiskCountObservation = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	fieldName: string;
}>('Risk/getRiskCountObservation');

export const getRiskCountProximity = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	layerName: string;
}>('Risk/getRiskCountProximity');

export const getRiskCountProximityAttribute = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	layerName: string;
	fieldName: string;
	valueType: string;
	distance: number;
}>('Risk/getRiskCountProximityAttribute');

export const getRiskCountCustomTable = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	fieldName: string;
	tableName: string;
	valueType: string;
}>('Risk/getRiskCountCustomTable');

export const getRiskCountTask = createAction<{
	key: string;
	metadata: ComponentMetadata[];
	metadataType: string;
	assetType: string;
	fieldName: string;
	tableName: string;
	valueType: string;
	inspectionType: string;
}>('Risk/getRiskCountTask');

export const getRiskCountScoreMatrix = createAction<{
	key: string;
	components: ComponentCount[];
	scoreMatrix: MultiParamCell[];
	assetType: string;
	inspectionType?: string;
}>('Risk/getRiskCountScoreMatrix');

export const getRiskCountResolved = createAction<RiskCountChange>(
	'Risk/getRiskCountResolved',
);

export const renameRiskCount = createAction<RiskCountRename>(
	'Risk/renameRiskCount',
);

//=== risk Matrix actions =====
export const setUpdatedRanges = createAction<RiskGradeRanges>(
	'Risk/setUpdatedRanges',
);
export const setDidMatrixUpdate = createAction<boolean>(
	'Risk/setDidMatrixUpdate',
);
export const setUpdatedScores = createAction<RiskMatrixGridObj[]>(
	'Risk/setUpdatedScores',
);
export const setRiskType = createAction<string>('Risk/setRiskType'); //switch between linear and matrix

export const initRiskMatrixPreview = createAction<InitRiskMatrixPreviewPayload>(
	'Risk/initRiskMatrixPreview',
);
export const cancelRiskMatrixPreview = createAction<{
	configId: string;
	matrix: RiskMatrixObj;
}>('Risk/cancelRiskMatrixPreview');
export const getRiskMatrixPreview = createAction<{
	configId: string;
	initialLoad?: boolean;
	cb?: (value: string) => void;
}>('Risk/getRiskMatrixPreview');
export const setEnablePreview = createAction<boolean>('Risk/setEnablePreview');
export const setCancellPreview = createAction<boolean>(
	'Risk/setCancellPreview',
);
export const setIsPreviewLoading = createAction<boolean>(
	'Risk/setIsPreviewLoading',
);
export const setIsPreviewOnLoad = createAction<boolean>(
	'Risk/setIsPreviewOnLoad',
);
export const setMatrixPreviewStatus = createAction<string>(
	'Risk/setmatrixPreviewStatus',
);
export const setRiskMatrixAfterPreview = createAction<RiskMatrixObj>(
	'Risk/setRiskMatrixAfterPreview',
);
export const setRefreshMatrixGrid = createAction<boolean>(
	'Risk/setRefreshMatrixGrid',
);
export const setEditedCellsList = createAction<PositionObj>(
	'Risk/updateEditedCellsList',
);
export const setOnlyGradesUpdated = createAction<boolean>(
	'Risk/setOnlyGradesUpdated',
);

export const clearMatrixUpdate = createAction<boolean>(
	'Risk/clearMatrixUpdate',
);

export const setOverallRisk = createAction<OverallRiskSetting>(
	'Risk/setOverallRisk',
);

//===================
